import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Package, Zap, Lock, Star, Trophy, Check, HelpCircle, Minus, Bot, Github, Crown, Users, Wrench, School } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { testimonials } from '@/lib/testimonials'; // Import testimonials
// Import product data and type
import {
  Product, // Import the Product type
  OWN_THE_AGENT,
  FROM_ADS_TO_AI,
  BUILD_THE_AGENT,
  SCRIPTS_MASTERY,
  MCC_PMAX_SCRIPT,
  SINGLE_PMAX_SCRIPT,
  NEGATIVE_KEYWORD_SCRIPT,
  MEGA_BUNDLE,
  IMPROVED_BUNDLE,
  features,
  EIGHTY_TWENTY_AGENT,
  FREE_SCRIPTS
} from '@/lib/product-data';
// Comparison Table Imports
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Create Testimonial Component
const Testimonial = ({ quote, author, role, image }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md transition-all hover:shadow-lg border border-gray-100 dark:border-gray-700 h-full flex flex-col">
    <div className="flex items-center mb-4">
      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xl font-bold mr-3 overflow-hidden">
        {image ? (
          <img src={image} alt={author} className="h-full w-full object-cover" />
        ) : (
          author.charAt(0)
        )}
      </div>
      <div>
        <h4 className="font-medium">{author}</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">{role}</p>
      </div>
    </div>
    <p
      className="italic text-gray-600 dark:text-gray-300 flex-1 text-sm"
      dangerouslySetInnerHTML={{ __html: `"${quote}"` }}
    />
  </div>
);

// Animated Product Card Component
const ProductCard: React.FC<{ product: Product, disableHover?: boolean }> = ({ product, disableHover }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isInternalRoute = product.link.startsWith('/');
  const Icon = product.icon || CheckCircle;

  // Base classes
  const baseBorder = "border";
  const hoverShadow = "hover:shadow-xl";
  const transition = "transition-all duration-300";

  // Theme colors mapping to Tailwind classes
  const themeClasses = {
    blue: {
      border: product.highlight ? "border-2 border-blue-500 dark:border-blue-400" : "border-blue-200 dark:border-blue-800",
      bg: "bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-gray-900",
      text: "text-blue-600 dark:text-blue-400",
      iconText: "text-blue-500",
      buttonBg: "bg-gradient-to-r from-blue-500 to-blue-600",
      buttonHoverBg: "hover:from-blue-600 hover:to-blue-700",
      buttonFadedBg: "bg-blue-500 opacity-70 cursor-not-allowed",
      checkMark: "text-blue-500",
      badge: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
    },
    purple: {
      border: product.highlight ? "border-2 border-purple-500 dark:border-purple-400" : "border-purple-200 dark:border-purple-800",
      bg: "bg-gradient-to-br from-purple-50 to-white dark:from-purple-950/30 dark:to-gray-900",
      text: "text-purple-600 dark:text-purple-400",
      iconText: "text-purple-500",
      buttonBg: "bg-gradient-to-r from-purple-500 to-purple-600",
      buttonHoverBg: "hover:from-purple-600 hover:to-purple-700",
      buttonFadedBg: "bg-purple-500 opacity-70 cursor-not-allowed",
      checkMark: "text-purple-500",
      badge: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
    },
    pink: {
      border: product.highlight ? "border-2 border-pink-500 dark:border-pink-400" : "border-pink-200 dark:border-pink-800",
      bg: "bg-gradient-to-br from-pink-50 to-white dark:from-pink-950/30 dark:to-gray-900",
      text: "text-pink-600 dark:text-pink-400",
      iconText: "text-pink-500",
      buttonBg: "bg-gradient-to-r from-pink-500 to-pink-600",
      buttonHoverBg: "hover:from-pink-600 hover:to-pink-700",
      buttonFadedBg: "bg-pink-500 opacity-70 cursor-not-allowed",
      checkMark: "text-pink-500",
      badge: "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200"
    },
    indigo: {
      border: product.highlight ? "border-2 border-indigo-500 dark:border-indigo-400" : "border-indigo-200 dark:border-indigo-800",
      bg: "bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/30 dark:to-gray-900",
      text: "text-indigo-600 dark:text-indigo-400",
      iconText: "text-indigo-500",
      buttonBg: "bg-gradient-to-r from-indigo-500 to-indigo-600",
      buttonHoverBg: "hover:from-indigo-600 hover:to-indigo-700",
      buttonFadedBg: "bg-indigo-500 opacity-70 cursor-not-allowed",
      checkMark: "text-indigo-500",
      badge: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
    },
    // Add orange theme
    orange: {
      border: product.highlight ? "border-2 border-orange-500 dark:border-orange-400" : "border-orange-200 dark:border-orange-800",
      bg: "bg-gradient-to-br from-orange-50 to-white dark:from-orange-950/30 dark:to-gray-900",
      text: "text-orange-600 dark:text-orange-400",
      iconText: "text-orange-500",
      buttonBg: "bg-gradient-to-r from-orange-500 to-orange-600",
      buttonHoverBg: "hover:from-orange-600 hover:to-orange-700",
      buttonFadedBg: "bg-orange-500 opacity-70 cursor-not-allowed",
      checkMark: "text-orange-500",
      badge: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
    },
    // Add green theme
    green: {
      border: product.highlight ? "border-2 border-green-500 dark:border-green-400" : "border-green-200 dark:border-green-800",
      bg: "bg-gradient-to-br from-green-50 to-white dark:from-green-950/30 dark:to-gray-900",
      text: "text-green-600 dark:text-green-400",
      iconText: "text-green-500",
      buttonBg: "bg-gradient-to-r from-green-500 to-green-600",
      buttonHoverBg: "hover:from-green-600 hover:to-green-700",
      buttonFadedBg: "bg-green-500 opacity-70 cursor-not-allowed",
      checkMark: "text-green-500",
      badge: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
    }
  };

  // Get theme classes
  const theme = themeClasses[product.themeColor as keyof typeof themeClasses] || themeClasses.blue;

  // Apply base styling and theme classes
  const cardClasses = cn(
    baseBorder,
    hoverShadow,
    transition,
    theme.border,
    theme.bg,
    "flex flex-col h-full",
    isHovered && !disableHover ? "transform translate-y-[-5px]" : ""
  );

  const titleClasses = cn("text-xl", product.highlight && theme.text);
  const priceClasses = cn("text-2xl font-bold", product.highlight && theme.text);
  const descriptionClasses = "text-sm mb-3 text-muted-foreground";

  // Re-add Star rating display
  const renderStarRating = () => {
    if (!product.rating) return null;

    return (
      <div className="flex items-center gap-1 mb-2">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={cn(
              "h-4 w-4",
              i < Math.floor(product.rating)
                ? "text-amber-500 fill-amber-500"
                : (i < product.rating
                  ? "text-amber-500 fill-amber-500 opacity-50"
                  : "text-gray-300 dark:text-gray-700")
            )}
          />
        ))}
        <span className="text-xs text-gray-500 ml-1">
          {product.rating.toFixed(1)} ({product.ratingCount})
        </span>
      </div>
    );
  };

  return (
    <Card
      className={cn(cardClasses)}
      onMouseEnter={() => !disableHover && setIsHovered(true)}
      onMouseLeave={() => !disableHover && setIsHovered(false)}
    >
      {/* Re-add discount/bestSeller badges */}
      {(product.discount || product.bestSeller) && (
        <div className="absolute -top-3 -right-3 z-10">
          {product.discount && (
            <div className="bg-green-500 text-white text-xs font-bold rounded-full px-3 py-1 shadow-lg">
              Save {product.discount.percentage}%
            </div>
          )}
          {product.bestSeller && !product.discount && (
            <div className="bg-amber-500 text-white text-xs font-bold rounded-full px-3 py-1 shadow-lg">
              Best Seller
            </div>
          )}
        </div>
      )}

      <CardHeader>
        <CardTitle className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <div className={cn("h-10 w-10 rounded-full flex items-center justify-center", theme.bg, theme.text)}>
              <Icon className={cn("h-5 w-5", theme.iconText)} />
            </div>
            <span className={titleClasses}>
              {product.name}
            </span>
          </div>
          <div className="text-right">
            {/* Re-add original price display */}
            {product.discount && (
              <div className="text-sm line-through text-gray-400 mb-1">
                {typeof product.discount.original === 'number' ? `€${product.discount.original}` : product.discount.original}
              </div>
            )}
            <span className={priceClasses}>
              {product.price}
            </span>
            {product.priceNote && <div className="text-xs text-muted-foreground">{product.priceNote}</div>}
          </div>
        </CardTitle>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mt-2">
          {product.type === 'bundle' && (
            <Badge variant="secondary" className={cn("w-fit", theme.badge)}>
              Best Value
            </Badge>
          )}
          {product.type === 'membership' && (
            <Badge variant="secondary" className={cn("w-fit", theme.badge)}>
              Membership
            </Badge>
          )}
          {product.limited && (
            <Badge variant="secondary" className={cn("w-fit", theme.badge)}>
              Limited Spots
            </Badge>
          )}
          {product.type === 'premium' && (
            <Badge variant="secondary" className={cn("w-fit", theme.badge)}>
              Elite
            </Badge>
          )}
          {/* Add Free badge - check price instead of type */}
          {product.price === 'Free' && (
            <Badge variant="secondary" className={cn("w-fit", theme.badge)}>
              Free
            </Badge>
          )}
          {/* Re-add featured badge */}
          {product.featured && (
            <Badge variant="secondary" className={cn("w-fit", theme.badge)}>
              Featured
            </Badge>
          )}
        </div>

        {/* Re-add Star Rating render call */}
        {renderStarRating()}
      </CardHeader>

      <CardContent className="flex-1">
        {product.description && (
          <p className={descriptionClasses}>{product.description}</p>
        )}

        {/* Always use 2-column layout for extras if they exist */}
        {product.extras && product.extras.length > 0 && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm"> {/* Use text-sm here */}
            {/* Left column */}
            <ul className="space-y-1"> {/* Removed text-sm from here */}
              {product.extras.slice(0, Math.ceil(product.extras.length / 2)).map((extra, i) => (
                <li key={`extra-l-${i}`} className="flex items-start gap-2 group">
                  <CheckCircle className={cn(
                    "h-4 w-4 mt-0.5 flex-shrink-0",
                    theme.checkMark,
                    "transition-transform duration-200 group-hover:scale-110"
                  )} />
                  <span>{extra}</span>
                </li>
              ))}
            </ul>

            {/* Right column */}
            <ul className="space-y-1"> {/* Removed text-sm from here */}
              {product.extras.slice(Math.ceil(product.extras.length / 2)).map((extra, i) => (
                <li key={`extra-r-${i}`} className="flex items-start gap-2 group">
                  <CheckCircle className={cn(
                    "h-4 w-4 mt-0.5 flex-shrink-0",
                    theme.checkMark,
                    "transition-transform duration-200 group-hover:scale-110"
                  )} />
                  <span>{extra}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-0 mt-4">
        {product.soldOut ? (
          <div className={cn(
            "inline-block w-full text-center px-4 py-3 rounded text-white font-medium",
            theme.buttonFadedBg
          )}>
            Sold Out
          </div>
        ) : (
          <a
            href={product.link}
            {...(!isInternalRoute ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            className={cn(
              "inline-block w-full text-center px-4 py-3 rounded text-white font-medium",
              "transition-all duration-300 transform hover:scale-[1.02]",
              "shadow-md hover:shadow-lg",
              theme.buttonBg,
              theme.buttonHoverBg
            )}
          >
            {product.type === 'membership' ? 'Claim a Founding Membership' :
              product.type === 'premium' ? 'Learn More' : 'Get Access'}
          </a>
        )}
      </CardFooter>
    </Card>
  );
};



// Hero Section Component
const HeroSection = () => {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20  md:py-8">
      {/* Background decoration */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-400/10 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 hover:bg-blue-200 transition-colors">
            Limited Time Offer - Special Launch Pricing
          </Badge>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Gain Rare & Valuable Skills.<br />Transform Your Career.
          </h1>

          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8">
            A complete ecosystem for Google Ads specialists & digital marketers ready to master AI
          </p>

          {/* --- REMOVED CountdownTimer --- */}

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a href="#products">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto">
                Explore Products
              </Button>
            </a>
            {/* --- REMOVED Watch Demo Button --- */}
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Trophy className="h-5 w-5 text-amber-500" />
            <span>Trusted by 12,480+ agencies worldwide</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Comparison Table Code START ---

// FeatureCell component remains the same
const FeatureCell = ({ value }: { value: boolean | 'partial' | 'upgradable' }) => {
  if (value === true) {
    return <Check className="h-5 w-5 text-green-500 mx-auto" />;
  } else if (value === 'partial') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <div className="text-amber-500 mx-auto flex items-center justify-center">
              <Check className="h-5 w-5" />*
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs max-w-[200px]">Partially included or available with limitations</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  } else if (value === 'upgradable') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <div className="text-blue-500 mx-auto flex items-center justify-center">
              <HelpCircle className="h-5 w-5" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs max-w-[200px]">Available as an upgrade for additional cost</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  } else {
    return <Minus className="h-5 w-5 text-red-500/50 mx-auto" />;
  }
};

function ProductComparison() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const tableVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, delay: 0.2 }
    }
  };


  return (
    <section className="py-16 bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={headingVariants}
        className="text-center mb-10 px-4"
      >
        <h2 className="text-3xl font-bold mb-3">What You Get at Each Level</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Explore the features included in our Foundation, Learning, Community, and Elite tiers.
        </p>
      </motion.div>

      <div className="min-w-[750px]">
        <div className="grid grid-cols-5 gap-4 mb-6 sticky top-16 bg-white dark:bg-gray-800/80 py-3 z-10 backdrop-blur-sm">
          <div className="font-semibold pl-2">Feature</div>
          <div className="text-center font-semibold text-blue-600 dark:text-blue-400">Foundation Level</div>
          <div className="text-center font-semibold text-pink-600 dark:text-pink-400">Learning Level</div>
          <div className="text-center font-semibold text-purple-600 dark:text-purple-400">Community Level</div>
          <div className="text-center font-semibold text-indigo-600 dark:text-indigo-400 pr-2">Elite Level</div>
        </div>

        <div className="space-y-2">
          {features.map((feature, index) => (
            <div key={feature.id} className={cn(
              "grid grid-cols-5 gap-4 py-3",
              index % 2 === 0 ? "bg-gray-50 dark:bg-gray-900/30" : "",
              "rounded-md items-center"
            )}>
              <div className="flex flex-col pl-2">
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <span>{feature.name}</span>
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger className="cursor-help">
                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                      </TooltipTrigger>
                      <TooltipContent side="top" align="start">
                        <p className="text-xs max-w-[250px]">
                          {feature.description || 'More details available.'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <FeatureCell value={feature.foundation} />
              </div>
              <div className="flex items-center justify-center">
                <FeatureCell value={feature.learning} />
              </div>
              <div className="flex items-center justify-center">
                <FeatureCell value={feature.community} />
              </div>
              <div className="flex items-center justify-center pr-2">
                <FeatureCell value={feature.elite} />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 text-xs text-muted-foreground">
          <p><span className="text-green-500 font-bold">✓</span> = Included | <span className="text-amber-500 font-bold">✓*</span> = Partially included</p>
        </div>
      </div>

    </section>
  );
}

// --- Comparison Table Code END ---

export default function BuyPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [randomTestimonials, setRandomTestimonials] = useState([]); // State for random testimonials

  // Keep the top 3 static
  const foundationTestimonials = testimonials.slice(0, 3); // Jyll, Alfred, Stacey

  // Effect to select random testimonials on mount
  useEffect(() => {
    const remainingTestimonials = testimonials.slice(3);
    const shuffled = [...remainingTestimonials]; // Clone array
    let currentIndex = shuffled.length, randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex !== 0) {
      // Pick a remaining element.
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [shuffled[currentIndex], shuffled[randomIndex]] = [
        shuffled[randomIndex], shuffled[currentIndex]];
    }

    // Select the first 3 from the shuffled array
    setRandomTestimonials(shuffled.slice(0, 3));
  }, []); // Empty dependency array means this runs only once on mount

  // Filter products based on active tab - uses imported products
  const getFilteredProducts = () => {
    // Use imported product constants directly
    const allProducts = [
      OWN_THE_AGENT,
      FROM_ADS_TO_AI,
      BUILD_THE_AGENT,
      SCRIPTS_MASTERY,
      MCC_PMAX_SCRIPT,
      SINGLE_PMAX_SCRIPT,
      NEGATIVE_KEYWORD_SCRIPT,
      // Add free products here as well for filtering
      EIGHTY_TWENTY_AGENT,
      FREE_SCRIPTS
    ];
    if (activeTab === "all") return allProducts;
    // Filter by tier, including 'free'
    return allProducts.filter(product => product.tier === activeTab);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">

      <HeroSection />

      <div className="container mx-auto px-4 py-16">

        {/* Hero Section with Growth Path */}
        <div id="benefits" className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20 items-center">
          {/* Left: Growth Path Description */}
          <div className="flex flex-col justify-center order-2 md:order-1">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-amber-500" /> Your AI & Google Ads Growth Path
            </h2>
            <p className="mb-6 text-gray-600 dark:text-gray-400">
              No matter where you are in your career, there's a perfect entry point for you. Start with what you need now and progress at your own pace to build truly valuable skills.
            </p>

            <ol className="space-y-6">
              <li className="flex gap-4 group">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-md group-hover:shadow-lg">1</div>
                <div>
                  <h3 className="font-medium text-blue-600 dark:text-blue-400 text-lg">Foundation Level - Premium Scripts</h3>
                  <p className="text-gray-600 dark:text-gray-400">Ready-to-use scripts and tools that save time and improve results immediately.</p>
                </div>
              </li>

              <li className="flex gap-4 group">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 text-white flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-md group-hover:shadow-lg">2</div>
                <div>
                  <h3 className="font-medium text-pink-600 dark:text-pink-400 text-lg">Learning Level - Workshops & Courses</h3>
                  <p className="text-gray-600 dark:text-gray-400">Develop the skills to create your own automation tools and leverage AI effectively.</p>
                </div>
              </li>

              <li className="flex gap-4 group">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 text-white flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-md group-hover:shadow-lg">3</div>
                <div>
                  <h3 className="font-medium text-purple-600 dark:text-purple-400 text-lg">Community Level - Support & Ongoing Education</h3>
                  <p className="text-gray-600 dark:text-gray-400">Join a dedicated group for ongoing support, learning, and professional growth.</p>
                </div>
              </li>

              <li className="flex gap-4 group">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 text-white flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-md group-hover:shadow-lg">4</div>
                <div>
                  <h3 className="font-medium text-indigo-600 dark:text-indigo-400 text-lg">Elite Level - Business Transformation</h3>
                  <p className="text-gray-600 dark:text-gray-400">Premium transformation with personalized guidance and exclusive resources.</p>
                </div>
              </li>
            </ol>
          </div>

          {/* Right: Pyramid Image - Simplified */}
          <div className="flex items-center justify-center order-1 md:order-2">
            <img
              src="/images/pyramid.png"
              alt="AI Consultancy Tiered Offerings"
              className="w-full max-w-md h-auto rounded-md"
            />
          </div>
        </div>

        {/* Special Featured Bundle - Restored original structure and position */}
        <div className="relative mb-20">
          <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-purple-600 rounded-lg blur opacity-25"></div>
          <div className="relative bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-1 flex flex-col justify-center">
                <div className="inline-block p-3 bg-orange-100 dark:bg-orange-900/50 rounded-full mb-4 w-fit">
                  <Package className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                </div>
                <h2 className="text-2xl font-semibold mb-3 text-orange-600 dark:text-orange-400">
                  Best Value Bundle
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Get the complete ecosystem at an incredible discount. Perfect for those serious about transforming their career.
                </p>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg w-fit">
                  <p className="text-sm flex items-center">
                    <Star className="h-4 w-4 text-amber-500 fill-amber-500 mr-2" />
                    <span className="font-medium">Limited time offer</span>
                  </p>
                </div>
              </div>
              <div className="md:col-span-2">
                <ProductCard product={IMPROVED_BUNDLE} />
              </div>
            </div>
          </div>
        </div>

        {/* --- RE-ADDED: First Testimonial Section --- */}
        <div id="testimonials" className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">See What Others Achieved</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Real results from Google Ads professionals like you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {foundationTestimonials.map((testimonial, index) => (
              <Testimonial key={index} {...testimonial} />
            ))}
          </div>
        </div>

        {/* Product Filter Tabs */}
        <div id="products" className="mb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Update grid columns for 6 tabs */}
            <TabsList className="grid grid-cols-6 w-full max-w-4xl mx-auto mb-10">
              <TabsTrigger value="all" className="text-center">All Products</TabsTrigger>
              <TabsTrigger value="foundation" className="text-center">Foundation</TabsTrigger>
              <TabsTrigger value="learning" className="text-center">Learning</TabsTrigger>
              <TabsTrigger value="community" className="text-center">Community</TabsTrigger>
              <TabsTrigger value="elite" className="text-center">Elite</TabsTrigger>
              {/* Add Free tab trigger */}
              <TabsTrigger value="free" className="text-center">Free</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="mt-0">
              {/* RESTORED Content for All Tab */}
              <>
                {/* TIER 4: Elite Level */}
                <div className="relative mb-16">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                    <div className="md:col-span-2">
                      <div className="sticky top-24">
                        <h2 className="text-2xl font-semibold mb-4 text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                          <Crown className="h-6 w-6" /> Elite Level
                        </h2>
                        <p className="mb-4 text-gray-600 dark:text-gray-400">Premium transformation and personalized guidance for serious business growth.</p>
                        <ul className="list-disc pl-5 text-sm space-y-1 mb-4 text-gray-600 dark:text-gray-400">
                          <li>Very small group (max 15 members)</li>
                          <li>1-on-1 calls with Mike</li>
                          <li>Custom implementation for your business</li>
                          <li>WhatsApp access for direct support</li>
                        </ul>
                        <div className="p-4 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg border border-indigo-200 dark:border-indigo-800">
                          <p className="text-sm italic">"For those who want not just to learn but to transform their entire business operation with AI-powered solutions."</p>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <ProductCard product={OWN_THE_AGENT} />
                    </div>
                  </div>
                </div>

                {/* TIER 3: Community Level */}
                <div className="relative mb-16">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                    <div className="md:col-span-2">
                      <div className="sticky top-24">
                        <h2 className="text-2xl font-semibold mb-4 text-purple-600 dark:text-purple-400 flex items-center gap-2">
                          <Users className="h-6 w-6" /> Community Level
                        </h2>
                        <p className="mb-4 text-gray-600 dark:text-gray-400">Ongoing learning, networking, and support to continually upgrade your skills and stay ahead of the curve.</p>
                        <ul className="list-disc pl-5 text-sm space-y-1 mb-4 text-gray-600 dark:text-gray-400">
                          <li>Monthly masterclass webinars</li>
                          <li>Private community access</li>
                          <li>AI-powered tool templates</li>
                          <li>Priority support and feedback</li>
                        </ul>
                        <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                          <p className="text-sm italic">"The perfect way to ensure your skills never become outdated in the rapidly evolving world of AI and Google Ads."</p>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <ProductCard product={FROM_ADS_TO_AI} />
                    </div>
                  </div>
                </div>

                {/* TIER 2: Learning Level */}
                <div className="relative mb-16">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                    <div className="md:col-span-2">
                      <div className="sticky top-24">
                        <h2 className="text-2xl font-semibold mb-4 text-pink-600 dark:text-pink-400 flex items-center gap-2">
                          <School className="h-6 w-6" /> Learning Level
                        </h2>
                        <p className="mb-4 text-gray-600 dark:text-gray-400">Develop valuable skills that will set you apart in the digital marketing landscape and open new career opportunities.</p>
                        <ul className="list-disc pl-5 text-sm space-y-1 mb-4 text-gray-600 dark:text-gray-400">
                          <li>Hands-on workshops and training</li>
                          <li>Learn to build your own tools</li>
                          <li>Create custom AI solutions</li>
                          <li>Career-enhancing capabilities</li>
                        </ul>
                        <div className="p-4 bg-pink-50 dark:bg-pink-950/30 rounded-lg border border-pink-200 dark:border-pink-800">
                          <p className="text-sm italic">"Don't just use AI tools - learn to create them. These skills are rapidly becoming the most valuable in digital marketing."</p>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <div className="grid grid-cols-1 gap-6 mb-4">
                        <ProductCard product={BUILD_THE_AGENT} />
                        <ProductCard product={SCRIPTS_MASTERY} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* TIER 1: Foundation Level */}
                <div className="relative mb-16">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                    <div className="md:col-span-2">
                      <div className="sticky top-24">
                        <h2 className="text-2xl font-semibold mb-4 text-blue-600 dark:text-blue-400 flex items-center gap-2">
                          <Wrench className="h-6 w-6" /> Foundation Level
                        </h2>
                        <p className="mb-4 text-gray-600 dark:text-gray-400">Ready-to-use tools that deliver immediate results and efficiency gains for your Google Ads campaigns.</p>
                        <ul className="list-disc pl-5 text-sm space-y-1 mb-4 text-gray-600 dark:text-gray-400">
                          <li>No coding knowledge required</li>
                          <li>Immediate implementation</li>
                          <li>Proven by thousands of agencies</li>
                          <li>Ongoing support and updates</li>
                        </ul>
                        <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                          <p className="text-sm italic">"These scripts have saved our agency countless hours of work and dramatically improved our campaign performance. A true game-changer."</p>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <div className="grid grid-cols-1 gap-6 mb-4">
                        <ProductCard product={MCC_PMAX_SCRIPT} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <ProductCard product={SINGLE_PMAX_SCRIPT} />
                          <ProductCard product={NEGATIVE_KEYWORD_SCRIPT} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- Free Tools Section --- */}
                <div className="relative mb-16">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                    {/* Left Column: Description */}
                    <div className="md:col-span-2">
                      <div className="sticky top-24">
                        <h2 className="text-2xl font-semibold mb-4 text-green-600 dark:text-green-400 flex items-center gap-2">
                          <Github className="h-6 w-6" /> Free Tools & Resources
                        </h2>
                        <p className="mb-4 text-gray-600 dark:text-gray-400">
                          Get started with valuable resources and tools designed to help you optimize your Google Ads campaigns, completely free.
                        </p>
                        <ul className="list-disc pl-5 text-sm space-y-1 mb-4 text-gray-600 dark:text-gray-400">
                          <li>Instantly useful scripts</li>
                          <li>No commitment required</li>
                          <li>Perfect for exploring AI potential</li>
                          <li>Community-supported</li>
                        </ul>
                        <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                          <p className="text-sm italic">"Explore the power of automation and AI in Google Ads with these free, ready-to-use resources."</p>
                        </div>
                      </div>
                    </div>
                    {/* Right Column: Product Cards */}
                    <div className="md:col-span-3">
                      <div className="grid grid-cols-1 gap-6"> {/* Single column grid for cards */}
                        <ProductCard product={EIGHTY_TWENTY_AGENT} />
                        <ProductCard product={FREE_SCRIPTS} />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            </TabsContent>

            {/* Add TabsContent for Free tab */}
            <TabsContent value="free" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto"> {/* Centered grid */}
                {getFilteredProducts().map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </TabsContent>

            {/* Existing TabsContent for other tiers (only render filtered products) */}
            {["foundation", "learning", "community", "elite"].map(tier => (
              <TabsContent key={tier} value={tier} className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getFilteredProducts().map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        {/* FAQ Section */}
        <div id="faq" className="mb-16 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
          <h3 className="text-2xl font-semibold mb-8 text-center">Frequently Asked Questions</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">?</div>
                Which product should I start with?
              </h4>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                If you're looking for immediate results, start with the scripts. For career growth, consider the a workshop. For ongoing AI education, support and community, join From Ads to AI membership.
              </p>

              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">?</div>
                Can I upgrade later?
              </h4>
              <p className="text-gray-600 dark:text-gray-400">
                Yes! The ecosystem is designed for you to start at any level and move up. Previous purchases are often credited toward higher-tier offerings unless they have early-bird or special launch pricing. Details are in Circle.
              </p>
            </div>

            <div>
              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">?</div>
                Do I need coding experience?
              </h4>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                No coding experience is needed for the scripts. For courses, we teach you everything from scratch. The learning curve is designed to be accessible to everyone.
              </p>

              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">?</div>
                What's the difference between the tiers?
              </h4>
              <p className="text-gray-600 dark:text-gray-400">
                Foundation products are ready-to-use tools. Learning products teach you to create your own. Community provides ongoing support and updates. Elite offers personalized guidance to transform your agency/business.
              </p>
            </div>
          </div>
        </div>

        {/* --- ADDED: Comparison Table Section --- */}
        <ProductComparison />

        {/* --- Random Testimonial Section (after FAQs/Comparison) --- */}
        <div className="mb-20 mt-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Map over the randomTestimonials state */}
            {randomTestimonials.length > 0 ? (
              randomTestimonials.map((testimonial, index) => (
                <Testimonial key={testimonial.author || index} {...testimonial} />
              ))
            ) : (
              <p className="text-center md:col-span-3 text-gray-500">Loading testimonials...</p> // Placeholder
            )}
          </div>
        </div>


        {/* CTA Section - UPDATED */}
        <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-8 md:p-12 rounded-2xl shadow-xl">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
          </div>

          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Join the Founding Members of <br />From Ads to AI™</h2>
            <p className="text-lg mb-8">
              Secure your spot in our exclusive community during this special launch period. Get ongoing learning, support, and networking to stay ahead in the AI era.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={FROM_ADS_TO_AI.link} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto">
                  Claim Your Founding Membership Now
                </Button>
              </a>
              {/* Optional: Add a secondary button if needed, e.g., Learn More */}
              {/* <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 transition-all duration-300">
                Learn More About the Membership
              </Button> */}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}